import { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '~/components/Icon/Icon';
import EditorButton from '~/components/editor/EditorButton/EditorButton';
import EditorCheckbox from '~/components/editor/EditorCheckbox/EditorCheckbox';
import EditorNumber from '~/components/editor/EditorNumber/EditorNumber';
import EditorSelect from '~/components/editor/EditorSelect/EditorSelect';
import EditorText from '~/components/editor/EditorText/EditorText';
import Checkbox from '~/components/widgets/Checkbox/Checkbox';
import Collapsible from '~/components/widgets/Collapsible/Collapsible';
import {
  channelCanConfigureDetuning,
  isDeviceConfigurable
} from '~/models/device';
import { Basis, getChannelKind } from '~/models/studio';
import { useExperiment, waveformsDuration } from '~/systems/experiment';
import { useUiState } from '~/systems/uiState';
import { assert } from '~/util';
import type { Channel } from '~/models/studio';
import type { ExperimentTx } from '~/systems/experiment';
import './ChannelEdit.css';

interface ChannelEditProps {
  channel: Channel;
}

const basisOptions = [
  { label: 'Digital', value: Basis.Digital },
  { label: 'Ground Rydberg', value: Basis.GroundRydberg }
];

const addressingOptions = [
  { label: 'Local', value: 'local' },
  { label: 'Global', value: 'global' }
];

export default function ChannelEdit({ channel }: ChannelEditProps) {
  const { commitedChannels, device, openTransaction, pulses } = useExperiment();
  const uiState = useUiState();
  const [collapsibleOpened, setCollapsibleOpened] = useState(false);
  const collapsibleRef = useRef<{ open(): void; close(): void }>(null);
  const channelHeadRef = useRef<HTMLDivElement>(null);

  const txRef = useRef<ExperimentTx | null>(null);
  const cmdIdxRef = useRef(0);

  function getEditTx() {
    if (txRef.current == null || !txRef.current.isOpen()) {
      txRef.current = openTransaction('channelEdit');
      cmdIdxRef.current = txRef.current.push({
        kind: 'update_channel',
        uuid: channel.uuid,
        props: {}
      });
    }
    return txRef.current;
  }

  function updateChannel(
    properties: Partial<Omit<Channel, 'uuid'>>,
    commitImmediately = false
  ) {
    getEditTx().update(cmdIdxRef.current, (cmd) => {
      assert(cmd.kind === 'update_channel');
      const ch = commitedChannels.find((c) => c.uuid === cmd.uuid);
      if (ch == null) {
        return cmd;
      }
      return { ...cmd, props: properties };
    });
    if (commitImmediately) {
      commit();
    }
  }

  function commit() {
    if (txRef.current != null && txRef.current.isOpen()) {
      txRef.current.commit();
    }
  }

  // Commit on unmount
  useEffect(() => () => commit(), []);

  function removeChannel() {
    commit(); // flush any open edit transaction first
    const tx = openTransaction('removeChannel');
    tx.push({ kind: 'remove_channel', uuid: channel.uuid });
    tx.commit();
  }

  const canEditHardwareParameters =
    isDeviceConfigurable(device) &&
    pulses.findIndex((p) => p.channelUuid === channel.uuid) === -1;

  const isReadonlyChannel = channel.basis === Basis.XY;
  const canEditParamaters = canEditHardwareParameters && !isReadonlyChannel;
  const basisSelectOptions = isReadonlyChannel
    ? [...basisOptions, { label: 'XY', value: Basis.XY }]
    : basisOptions;

  const isSelected = useCallback(
    () => uiState.selectedChannel === channel.uuid,
    [channel.uuid, uiState.selectedChannel]
  );

  function scrollIntoView() {
    channelHeadRef.current?.scrollIntoView();
  }

  // Watch selectedChannel changes
  const prevSelectedRef = useRef(isSelected());
  useEffect(() => {
    const selected = isSelected();
    const wasSelected = prevSelectedRef.current;
    prevSelectedRef.current = selected;
    if (!selected && wasSelected) {
      collapsibleRef.current?.close();
    } else if (selected) {
      collapsibleRef.current?.open();
      setTimeout(() => scrollIntoView(), 0);
    }
  }, [isSelected, uiState.selectedChannel]);

  // On mount scroll into view if selected
  useEffect(() => {
    if (isSelected()) {
      scrollIntoView();
    }
  }, [isSelected]);

  function toggleDetuning(toggle: boolean) {
    const currentChannelPulses = pulses.filter(
      (p) => p.channelUuid === channel.uuid
    );
    const tx = openTransaction('Channel Detuning');
    tx.push({
      kind: 'update_channel',
      uuid: channel.uuid,
      props: { enableDetuning: toggle }
    });
    for (const pulse of currentChannelPulses) {
      if (toggle) {
        tx.push({
          kind: 'update_pulse',
          uuid: pulse.uuid,
          props: {
            detuning: [
              {
                kind: 'constant',
                constraint: {
                  kind: 'area',
                  maxVal: 0,
                  duration: waveformsDuration(pulse.amplitude)
                }
              }
            ]
          }
        });
      } else {
        tx.push({
          kind: 'update_pulse',
          uuid: pulse.uuid,
          props: { detuning: null }
        });
      }
    }
    tx.commit();
  }

  // Automatically add a UUID to field names to prevent duplicates
  const generateFieldName = (label: string) => {
    return `${label}-${channel.uuid}`;
  };

  const channelKind = getChannelKind(channel);

  return (
    <div
      ref={channelHeadRef}
      className={`ChannelEdit${collapsibleOpened ? ' is-opened' : ''}`}
    >
      <Collapsible
        ref={collapsibleRef}
        initialState={isSelected()}
        subcollapsible
        onUpdateOpened={setCollapsibleOpened}
        button={() => (
          <h3 className={`ChannelEdit-label ChannelEdit-label--${channelKind}`}>
            <Icon
              size={10}
              kind={
                channel.addressing === 'local'
                  ? 'channel-local'
                  : 'channel-global'
              }
            />
            <span className="ChannelEdit-kind">
              {channelKind} {channel.addressing}
            </span>
            <span>{channel.label}</span>
          </h3>
        )}
        items={
          <div className="ChannelEdit-body">
            {isDeviceConfigurable(device) &&
              channelCanConfigureDetuning(channel) &&
              !isReadonlyChannel && (
                <EditorCheckbox
                  label="Detuning"
                  name={generateFieldName('enable-detuning')}
                  value={channel.enableDetuning ?? false}
                  onChange={(v) => toggleDetuning(v)}
                />
              )}
            <EditorText
              label="Label"
              name={generateFieldName('channel-label')}
              value={channel.label}
              onChange={(v) => updateChannel({ label: v })}
              onBlur={commit}
            />
            <EditorSelect
              label="Basis"
              name={generateFieldName('basis')}
              options={basisSelectOptions}
              value={channel.basis}
              disabled={!canEditParamaters || isReadonlyChannel}
              onChange={(v) => updateChannel({ basis: v as Basis }, true)}
            />
            <EditorSelect
              label="Addressing"
              name={generateFieldName('addressing')}
              options={addressingOptions}
              value={channel.addressing}
              disabled={!canEditParamaters}
              onChange={(v) =>
                updateChannel({ addressing: v as 'local' | 'global' }, true)
              }
            />
            <EditorNumber
              label="Max Abs Detuning"
              name={generateFieldName('max-abs-detuning')}
              value={channel.maxAbsDetuning}
              step={0.001}
              max={Infinity}
              min={0.001}
              unit="rad/µs"
              disabled={!canEditParamaters}
              onChange={(v) => updateChannel({ maxAbsDetuning: v })}
              onBlur={commit}
            />
            <EditorNumber
              label="Max Amp"
              name={generateFieldName('max-amp')}
              value={channel.maxAmp}
              step={0.001}
              max={Infinity}
              min={0.001}
              unit="rad/µs"
              disabled={!canEditParamaters}
              onChange={(v) => updateChannel({ maxAmp: v })}
              onBlur={commit}
            />
            <EditorNumber
              label="Phase Jump Time"
              name={generateFieldName('phase-jump-time')}
              value={channel.phaseJumpTime}
              step={0.001}
              max={Infinity}
              min={0}
              unit="ns"
              disabled={!canEditParamaters}
              onChange={(v) => updateChannel({ phaseJumpTime: v })}
              onBlur={commit}
            />
            <EditorNumber
              label="Clock Period"
              name={generateFieldName('clock-period')}
              value={channel.clockPeriod}
              step={0.001}
              max={Infinity}
              min={0}
              unit="ns"
              disabled={!canEditParamaters}
              onChange={(v) => updateChannel({ clockPeriod: v })}
              onBlur={commit}
            />
            <EditorNumber
              label="Min Duration"
              name={generateFieldName('min-duration')}
              value={channel.minDuration}
              step={1}
              max={channel.maxDuration ?? 10000}
              min={1}
              unit="ns"
              disabled={!canEditParamaters}
              onChange={(v) => updateChannel({ minDuration: v })}
              onBlur={commit}
            />
            <div className="ChannelEdit-optionalFieldRow">
              <EditorNumber
                label="Min Retarget Interval"
                name={generateFieldName('min-retarget-interval')}
                value={channel.minRetargetInterval ?? 0}
                step={0.001}
                max={Infinity}
                min={0}
                unit="ns"
                disabled={!canEditParamaters}
                onChange={(v) => updateChannel({ minRetargetInterval: v })}
                onBlur={commit}
              />
              <Checkbox
                value={channel.minRetargetInterval !== undefined}
                disabled={!canEditParamaters}
                onChange={(v) =>
                  updateChannel(
                    { minRetargetInterval: v ? 0 : undefined },
                    true
                  )
                }
              />
            </div>
            <div className="ChannelEdit-optionalFieldRow">
              <EditorNumber
                label="Fixed Retarget Time"
                name={generateFieldName('fixed-retarget-time')}
                value={channel.fixedRetargetT ?? 0}
                step={0.001}
                max={Infinity}
                min={0}
                unit="ns"
                disabled={!canEditParamaters}
                onChange={(v) => updateChannel({ fixedRetargetT: v })}
                onBlur={commit}
              />
              <Checkbox
                value={channel.fixedRetargetT !== undefined}
                disabled={!canEditParamaters}
                onChange={(v) =>
                  updateChannel({ fixedRetargetT: v ? 0 : undefined }, true)
                }
              />
            </div>
            <div className="ChannelEdit-optionalFieldRow">
              <EditorNumber
                label="Max Targets"
                name={generateFieldName('max-targets')}
                value={channel.maxTargets ?? 0}
                step={0.001}
                max={Infinity}
                min={0}
                unit="ns"
                disabled={!canEditParamaters}
                onChange={(v) => updateChannel({ maxTargets: v })}
                onBlur={commit}
              />
              <Checkbox
                value={channel.maxTargets !== undefined}
                disabled={!canEditParamaters}
                onChange={(v) =>
                  updateChannel({ maxTargets: v ? 0 : undefined }, true)
                }
              />
            </div>
            <div className="ChannelEdit-optionalFieldRow">
              <EditorNumber
                label="Max Duration"
                name={generateFieldName('max-duration')}
                value={channel.maxDuration ?? 0}
                step={1}
                max={10000}
                min={channel.minDuration}
                unit="ns"
                disabled={!canEditParamaters}
                onChange={(v) => updateChannel({ maxDuration: v })}
                onBlur={commit}
              />
              <Checkbox
                value={channel.maxDuration !== undefined}
                disabled={!canEditParamaters}
                onChange={(v) =>
                  updateChannel(
                    { maxDuration: v ? (channel.minDuration ?? 1) : undefined },
                    true
                  )
                }
              />
            </div>
            <div className="ChannelEdit-optionalFieldRow">
              <EditorNumber
                label="Mod Bandwidth"
                name={generateFieldName('mod-bandwidth')}
                value={channel.modBandwidth ?? 0}
                step={0.001}
                max={Infinity}
                min={0}
                unit="ns"
                disabled={!canEditParamaters}
                onChange={(v) => updateChannel({ modBandwidth: v })}
                onBlur={commit}
              />
              <Checkbox
                value={channel.modBandwidth !== undefined}
                disabled={!canEditParamaters}
                onChange={(v) =>
                  updateChannel({ modBandwidth: v ? 0 : undefined }, true)
                }
              />
            </div>
            <EditorButton disabled={!canEditParamaters} onClick={removeChannel}>
              remove channel
            </EditorButton>
          </div>
        }
      />
    </div>
  );
}
